import { useState, useMemo } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { format, subDays, isAfter } from "date-fns";
import { KG_TO_LBS, CM_TO_IN } from "@/hooks/useUnitPrefs";

export default function MomentumChart({
  logs = [],
  useLbs = true,
  useInches = true,
  weightGoal = null,
}) {
  const [activeMetric, setActiveMetric] = useState("weight"); // 'weight' | 'body_fat' | 'waist'

  // Count total entries available for the active metric across all time
  const totalMetricEntries = useMemo(() => {
    return logs.filter((l) => {
      if (!l || !l.date) return false;
      if (activeMetric === "weight") return l.weight_kg != null;
      if (activeMetric === "body_fat") return l.body_fat_percentage != null;
      if (activeMetric === "waist") return l.waist_cm != null;
      return false;
    }).length;
  }, [logs, activeMetric]);

  // If user has entries for the active metric, but none in the last 31 days, default to 'all'
  const [timeRange, setTimeRange] = useState(() => {
    const now = new Date();
    const hasRecent = logs.some((l) => {
      if (!l || !l.date || l.weight_kg == null) return false;
      try {
        const d = new Date(l.date + "T00:00:00");
        return isAfter(d, subDays(now, 31));
      } catch {
        return false;
      }
    });
    return hasRecent ? "1M" : "all";
  });

  // Determine current display month/year for top-right of section
  const currentMonthYear = useMemo(() => {
    if (logs.length > 0 && logs[0]?.date) {
      try {
        return format(new Date(logs[0].date + "T00:00:00"), "MMMM yyyy");
      } catch {
        // fallback
      }
    }
    return format(new Date(), "MMMM yyyy");
  }, [logs]);

  // Filter and process chart data
  const { chartData, metricLabel, unitLabel, summaryNote } = useMemo(() => {
    // 1. Sort logs chronologically (oldest to newest)
    const sorted = [...logs]
      .filter((l) => l && l.date)
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    // 2. Filter by timeRange
    const now = new Date();
    const filtered = sorted.filter((l) => {
      if (timeRange === "all") return true;
      try {
        const logDate = new Date(l.date + "T00:00:00");
        if (timeRange === "1W") {
          return isAfter(logDate, subDays(now, 7));
        }
        if (timeRange === "1M") {
          return isAfter(logDate, subDays(now, 31));
        }
      } catch {
        return true;
      }
      return true;
    });

    // 3. Extract the requested metric
    let mLabel = "Weight";
    let uLabel = useLbs ? "lbs" : "kg";

    if (activeMetric === "body_fat") {
      mLabel = "Body fat";
      uLabel = "%";
    } else if (activeMetric === "waist") {
      mLabel = "Waist";
      uLabel = useInches ? "in" : "cm";
    }

    const dataPoints = filtered
      .map((l) => {
        let rawVal = null;
        if (activeMetric === "weight") {
          if (l.weight_kg != null) {
            rawVal = useLbs ? +(l.weight_kg * KG_TO_LBS).toFixed(1) : +l.weight_kg.toFixed(1);
          }
        } else if (activeMetric === "body_fat") {
          if (l.body_fat_percentage != null) {
            rawVal = +l.body_fat_percentage.toFixed(1);
          }
        } else if (activeMetric === "waist") {
          if (l.waist_cm != null) {
            rawVal = useInches ? +(l.waist_cm * CM_TO_IN).toFixed(1) : +l.waist_cm.toFixed(1);
          }
        }

        if (rawVal == null) return null;

        let displayDate = l.date;
        try {
          displayDate = format(new Date(l.date + "T00:00:00"), "MMM d");
        } catch {
          // keep l.date
        }

        return {
          id: l.id,
          dateStr: l.date,
          date: displayDate,
          value: rawVal,
        };
      })
      .filter(Boolean);

    // 4. Calculate dynamic summary note at bottom
    let note = "Keep logging check-ins to reveal your momentum.";
    if (dataPoints.length >= 2) {
      const first = dataPoints[0].value;
      const last = dataPoints[dataPoints.length - 1].value;
      const diff = +(last - first).toFixed(1);
      const count = dataPoints.length;
      const periodName = timeRange === "1W" ? "this week" : timeRange === "1M" ? "this month" : "overall";

      if (activeMetric === "weight") {
        if (diff < 0) {
          note = `${Math.abs(diff)} ${uLabel} down across ${count} check-ins ${periodName}.`;
        } else if (diff > 0) {
          note = `${diff} ${uLabel} up across ${count} check-ins ${periodName}.`;
        } else {
          note = `Weight remained steady across ${count} check-ins ${periodName}.`;
        }
      } else if (activeMetric === "body_fat") {
        if (diff < 0) {
          note = `${Math.abs(diff)}% body fat down across ${count} check-ins ${periodName}.`;
        } else if (diff > 0) {
          note = `${diff}% body fat up across ${count} check-ins ${periodName}.`;
        } else {
          note = `Body fat percentage steady across ${count} check-ins ${periodName}.`;
        }
      } else if (activeMetric === "waist") {
        if (diff < 0) {
          note = `${Math.abs(diff)} ${uLabel} reduction across ${count} check-ins ${periodName}.`;
        } else if (diff > 0) {
          note = `${diff} ${uLabel} increase across ${count} check-ins ${periodName}.`;
        } else {
          note = `Waist measurement steady across ${count} check-ins ${periodName}.`;
        }
      }
    } else if (dataPoints.length === 1) {
      note = `1 check-in recorded for ${metricLabel.toLowerCase()}. Log another check-in to see your momentum trend.`;
    }

    return {
      chartData: dataPoints,
      metricLabel: mLabel,
      unitLabel: uLabel,
      summaryNote: note,
    };
  }, [logs, activeMetric, timeRange, useLbs, useInches]);

  // Compute nice Y-domain
  const yDomain = useMemo(() => {
    if (chartData.length === 0) return [0, 100];
    const vals = chartData.map((d) => d.value);
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const padding = Math.max(1, (max - min) * 0.2);
    return [Math.floor(min - padding), Math.ceil(max + padding)];
  }, [chartData]);

  return (
    <div className="bg-[#131F31] border border-[#233148] rounded-[22px] p-5 sm:p-6 space-y-5">
      {/* Top Header: Title & Month */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white tracking-tight">See your momentum</h2>
        <span className="text-sm font-medium text-white/50">{currentMonthYear}</span>
      </div>

      {/* Segmented Metric Control: Weight | Body fat | Waist */}
      <div className="bg-[#0C1424] p-1 rounded-xl flex items-center justify-between gap-1 border border-[#233148]/60">
        <button
          type="button"
          onClick={() => setActiveMetric("weight")}
          className={`flex-1 py-2 text-xs sm:text-sm font-semibold rounded-lg transition-all ${
            activeMetric === "weight"
              ? "bg-[#1E2D44] text-white shadow-sm"
              : "text-white/50 hover:text-white/80"
          }`}
        >
          Weight
        </button>
        <button
          type="button"
          onClick={() => setActiveMetric("body_fat")}
          className={`flex-1 py-2 text-xs sm:text-sm font-semibold rounded-lg transition-all ${
            activeMetric === "body_fat"
              ? "bg-[#1E2D44] text-white shadow-sm"
              : "text-white/50 hover:text-white/80"
          }`}
        >
          Body fat
        </button>
        <button
          type="button"
          onClick={() => setActiveMetric("waist")}
          className={`flex-1 py-2 text-xs sm:text-sm font-semibold rounded-lg transition-all ${
            activeMetric === "waist"
              ? "bg-[#1E2D44] text-white shadow-sm"
              : "text-white/50 hover:text-white/80"
          }`}
        >
          Waist
        </button>
      </div>

      {/* Sub Header: Time-range filters & Unit label */}
      <div className="flex items-center justify-between pt-1">
        <div className="flex items-center gap-1.5">
          {(["1W", "1M", "all"]).map((r) => {
            const label = r === "all" ? "All" : r;
            const isSelected = timeRange === r;
            return (
              <button
                key={r}
                type="button"
                onClick={() => setTimeRange(r)}
                className={`px-3 py-1 text-xs font-semibold rounded-lg transition-all ${
                  isSelected
                    ? "bg-[#13435C] text-[#36D0FF] border border-[#36D0FF]/40"
                    : "text-white/50 hover:text-white/80 bg-transparent"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
        <div className="text-xs text-white/50 font-medium">
          {metricLabel} · {unitLabel}
        </div>
      </div>

      {/* Chart Area */}
      <div className="h-52 w-full pt-2">
        {chartData.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center p-4 border border-dashed border-[#233148] rounded-xl text-white/40">
            <p className="text-sm font-medium">No {metricLabel.toLowerCase()} entries in this range</p>
            {timeRange !== "all" && totalMetricEntries > 0 ? (
              <button
                type="button"
                onClick={() => setTimeRange("all")}
                className="text-xs text-[#36D0FF] hover:underline mt-1.5 font-medium"
              >
                View all historical entries ({totalMetricEntries}) →
              </button>
            ) : (
              <p className="text-xs mt-1">Tap “+ Log progress” above to record a check-in</p>
            )}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 12, right: 10, left: -22, bottom: 0 }}>
              <defs>
                <linearGradient id="momentumGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#36D0FF" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#36D0FF" stopOpacity={0.0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#233148" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: "rgba(255,255,255,0.4)" }}
                axisLine={false}
                tickLine={false}
                dy={6}
              />
              <YAxis
                domain={yDomain}
                tick={{ fontSize: 11, fill: "rgba(255,255,255,0.4)" }}
                axisLine={false}
                tickLine={false}
                dx={-4}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const item = payload[0].payload;
                    return (
                      <div className="bg-[#0C1424] border border-[#233148] px-3 py-2 rounded-xl shadow-lg text-xs">
                        <p className="text-white/50">{item.date}</p>
                        <p className="text-[#36D0FF] font-bold text-sm mt-0.5">
                          {item.value} {unitLabel}
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#36D0FF"
                strokeWidth={3}
                fill="url(#momentumGradient)"
                dot={{
                  r: 4.5,
                  fill: "#131F31",
                  stroke: "#36D0FF",
                  strokeWidth: 2,
                }}
                activeDot={{
                  r: 6.5,
                  fill: "#36D0FF",
                  stroke: "#FFFFFF",
                  strokeWidth: 2,
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Dynamic Summary Note */}
      <div className="pt-1">
        <p className="text-xs sm:text-sm font-medium text-[#36D0FF]/90 flex items-center gap-1.5">
          {summaryNote}
        </p>
      </div>
    </div>
  );
}
